// src/pages/Profile/components/AlertsTab.js - WITH ALL NOTIFICATION TYPES
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../../hooks/useProfile';

/* ── Small inline logo loader ── */
const MiniLogoLoader = ({ label }) => (
  <div className="mini-loader-wrap">
    <div className="mini-logo">
      <div className="mini-bar-wrap mini-bar-left">
        <div className="mini-bar-fill mini-fill-left" />
      </div>
      <div className="mini-bar-wrap mini-bar-center">
        <div className="mini-bar-fill mini-fill-center" />
      </div>
      <div className="mini-bar-wrap mini-bar-right">
        <div className="mini-bar-fill mini-fill-right" />
      </div>
    </div>
    {label && <p className="mini-loader-label">{label}</p>}
  </div>
);

const AlertsTab = ({ alerts, loading, error }) => {
  const navigate = useNavigate();
  const { markNotificationRead, markAllNotificationsRead, fetchAlerts } = useProfile();
  
  const [displayCount, setDisplayCount] = useState(5);
  const ITEMS_PER_PAGE = 5;

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getAlertType = (notificationType, isProcessed) => {
    // Wallet, deposit/withdrawal rejection, price alerts:
    // return 'neutral' so no coloured left border is applied.
    // Colour is conveyed through the message text instead.
    if (notificationType === 'WALLET_CREDIT') return 'neutral';
    if (notificationType === 'WALLET_DEBIT') return 'neutral';
    if (notificationType === 'DEPOSIT_REJECTED') return 'neutral';
    if (notificationType === 'WITHDRAWAL_REJECTED') return 'neutral';
    if (notificationType === 'PRICE_ALERT') return 'neutral';

    if (isProcessed && notificationType === 'REMINDER_DUE') return 'processed';
    
    const typeMap = {
      'MATURITY': 'success',
      'WITHDRAWAL_DUE': 'info',
      'PROFIT_AVAILABLE': 'success',
      'PERIODIC_BUY_DUE': 'info',
      'PLAN_REMINDER': 'info',
      'REMINDER_DUE': 'actionable'
    };
    return typeMap[notificationType] || 'info';
  };

  const getAlertIcon = (notificationType, direction) => {
    if (notificationType === 'PRICE_ALERT') {
      return direction === 'UP' ? '📈' : '📉';
    }
    const iconMap = {
      'WALLET_CREDIT': '💳',
      'WALLET_DEBIT': '💸',
      'DEPOSIT_REJECTED': '🚫',
      'WITHDRAWAL_REJECTED': '↩️',
      'MATURITY': '🎉',
      'WITHDRAWAL_DUE': '💰',
      'PROFIT_AVAILABLE': '📈',
      'PERIODIC_BUY_DUE': '🔄',
      'PLAN_REMINDER': '⏰',
      'REMINDER_DUE': '💰'
    };
    return iconMap[notificationType] || '🔔';
  };

  const getNotificationSource = (alert) => {
    if (alert.notification_type === 'PRICE_ALERT') return 'price_alert';
    if (alert.withdrawal_notification) return 'withdrawal';
    if (alert.deposit_notification) return 'deposit';
    if (alert.wallet) return 'wallet';
    if (alert.investment) return 'investment';
    if (alert.plan || alert.plan_id) return 'plan';
    return 'investment';
  };

  // Wallet, deposit, withdrawal, and price-alert notifications are informational only
  const isActionable = (alert) => {
    if (alert.wallet) return false;
    if (alert.deposit_notification) return false;
    if (alert.withdrawal_notification) return false;
    if (alert.notification_type === 'PRICE_ALERT') return false;
    return alert.notification_type === 'REMINDER_DUE' && !alert.is_processed;
  };

  const handleAlertClick = async (alert) => {
    // Price alert: mark as read via watchlistService
    if (alert.notification_type === 'PRICE_ALERT') {
      if (!alert.is_read) {
        try {
          const { watchlistService } = await import('../../../services/watchlistService');
          await watchlistService.markPriceAlertsRead({ ids: [alert.id] });
          await fetchAlerts();
        } catch (err) {
          console.error('Error marking price alert as read:', err);
        }
      }
      return;
    }

    // Wallet: mark as read only
    if (alert.wallet) {
      if (!alert.is_read) {
        try { await markNotificationRead(alert.id, 'wallet'); }
        catch (err) { console.error('Error marking wallet notification as read:', err); }
      }
      return;
    }

    // Deposit rejection: mark as read only
    if (alert.deposit_notification) {
      if (!alert.is_read) {
        try { await markNotificationRead(alert.id, 'deposit'); }
        catch (err) { console.error('Error marking deposit notification as read:', err); }
      }
      return;
    }

    // Withdrawal rejection: mark as read only
    if (alert.withdrawal_notification) {
      if (!alert.is_read) {
        try { await markNotificationRead(alert.id, 'withdrawal'); }
        catch (err) { console.error('Error marking withdrawal notification as read:', err); }
      }
      return;
    }

    if (alert.notification_type === 'REMINDER_DUE' && alert.is_processed) {
      console.log('Cannot interact with processed notification');
      return;
    }

    try {
      if (!alert.is_read) {
        const source = getNotificationSource(alert);
        await markNotificationRead(alert.id, source);
      }

      if (alert.notification_type === 'REMINDER_DUE' && !alert.is_processed) {
        const planId = alert.plan_id || alert.plan?.id;
        if (planId) navigate(`/plan/contribute/${planId}`);
      } else if (alert.notification_type === 'MATURITY') {
        const planId = alert.plan_id || alert.plan?.id;
        if (planId) navigate(`/withdraw/plan/${planId}`);
      } else if (alert.notification_type === 'PERIODIC_BUY_DUE') {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error handling alert click:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // Mark investment/plan alerts as read
      await markAllNotificationsRead();
      // Mark price alerts as read
      const { watchlistService } = await import('../../../services/watchlistService');
      await watchlistService.markPriceAlertsRead({ mark_all: true });
      await fetchAlerts();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getProcessedBadge = (alert) => {
    if (alert.wallet) {
      return alert.notification_type === 'WALLET_CREDIT' ? 'Credit' : 'Debit';
    }
    if (alert.deposit_notification) return 'Rejected';
    if (alert.withdrawal_notification) return 'Rejected';
    if (alert.notification_type === 'REMINDER_DUE' && alert.is_processed) return 'Completed';
    return null;
  };

  const handleSeeMore = () => setDisplayCount(prev => prev + ITEMS_PER_PAGE);

  // Returns a CSS class that colours the message text green or red.
  // Only applied to wallet, deposit/withdrawal rejection, and price alerts.
  // Investment and plan notifications are left with the default dark colour.
  const getTitleColorClass = (alert) => {
    const t = alert.notification_type;
    if (t === 'WALLET_CREDIT') return 'text-positive';
    if (t === 'WALLET_DEBIT') return 'text-negative';
    if (t === 'DEPOSIT_REJECTED') return 'text-negative';
    if (t === 'WITHDRAWAL_REJECTED') return 'text-negative';
    if (t === 'PRICE_ALERT') return alert.direction === 'UP' ? 'text-positive' : 'text-negative';
    return ''; // investment, plan, maturity, reminder — untouched
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;
  const displayedAlerts = alerts.slice(0, displayCount);
  const hasMore = displayCount < alerts.length;

  return (
    <div className="profile-tab-content">
      <div className="tab-header">
        <div className="tab-header-left">
          <span className="tab-icon">🔔</span>
          <h2>Notifications</h2>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button 
            className="mark-all-read-button"
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <MiniLogoLoader label="Loading notifications..." />
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : alerts.length === 0 ? (
        <div className="empty-state">
          <p>No notifications</p>
        </div>
      ) : (
        <>
          {alerts.length > ITEMS_PER_PAGE && (
            <div className="pagination-info">
              Showing {displayedAlerts.length} of {alerts.length} notifications
            </div>
          )}

          <div className="alerts-list">
            {displayedAlerts.map((alert) => {
              const alertType = getAlertType(alert.notification_type, alert.is_processed);
              const processedBadge = getProcessedBadge(alert);
              const isInformational = alert.wallet || alert.deposit_notification || alert.withdrawal_notification || alert.notification_type === 'PRICE_ALERT';
              const clickable = isInformational
                ? !alert.is_read
                : !(alert.notification_type === 'REMINDER_DUE' && alert.is_processed);

              return (
                <div 
                  key={`${getNotificationSource(alert)}-${alert.id}`}
                  className={`alert-card ${alertType} ${alert.is_read ? 'read' : 'unread'} ${alert.wallet ? 'wallet-alert' : ''} ${alert.deposit_notification ? 'deposit-alert' : ''} ${alert.withdrawal_notification ? 'withdrawal-alert' : ''} ${alert.notification_type === 'PRICE_ALERT' ? `price-alert-card ${alert.direction === 'UP' ? 'price-up' : 'price-down'}` : ''} ${!clickable ? 'not-clickable' : ''}`}
                  onClick={() => handleAlertClick(alert)}
                  style={{ cursor: isInformational && alert.is_read ? 'default' : 'pointer' }}
                >
                  <div className="alert-icon-wrapper">
                    <span className="alert-type-icon">
                      {getAlertIcon(alert.notification_type, alert.direction)}
                    </span>
                    {!alert.is_read && !alert.is_processed && <div className="alert-indicator"></div>}
                  </div>
                  <div className="alert-content">
                    <h4 className={`alert-title ${getTitleColorClass(alert)}`}>
                      {alert.message}
                      {isActionable(alert) && (
                        <span className="actionable-badge">Action Required</span>
                      )}
                      {processedBadge && (
                        <span className={`processed-badge ${
                          alert.wallet
                            ? (alert.notification_type === 'WALLET_CREDIT' ? 'badge-credit' : 'badge-debit')
                            : (alert.deposit_notification || alert.withdrawal_notification)
                              ? 'badge-rejected'
                              : ''
                        }`}>
                          {processedBadge}
                        </span>
                      )}
                    </h4>
                    <div className="alert-meta">
                      <span className="alert-time">{formatTimeAgo(alert.created_at)}</span>
                      <span className="alert-type-label">
                        {alert.notification_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  {isActionable(alert) ? (
                    <div className="alert-arrow">→</div>
                  ) : processedBadge && !isInformational ? (
                    <div className="processed-icon">✓</div>
                  ) : (
                    !alert.is_read && <div className="mark-read-icon">✓</div>
                  )}
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="pagination-controls">
              <button 
                className="see-more-button"
                onClick={handleSeeMore}
              >
                See More ({alerts.length - displayCount} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AlertsTab;